import { useState } from "react";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { Card } from "components/ui/Card";
import { Paragraph } from "common/ui/Paragraph";
import { Button } from "common/ui/Button";
import { Stack } from "common/ui/Stack";
import { Heading } from "common/ui/Heading";
import { Input } from "common/ui/Input";

import { requestOtp } from "common/middleware/api.auth";
import { emailValidation } from "common/middleware/validation";
import { Logger } from "common/middleware/logger";

import styles from "./OtpRequestForm.module.scss";

interface OtpRequestFormProps {
  redirectUrl?: string;
  onSuccess?: () => void;
}

export const OtpRequestForm = ({
  redirectUrl,
  onSuccess,
}: OtpRequestFormProps) => {
  const { t } = useTranslation(["auth", "validation"]);
  const router = useRouter();
  const [validationError, setValidationError] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    watch,
  } = useForm({
    mode: "onBlur",
    resolver: yupResolver(emailValidation),
  });

  const email = watch("email");

  const sendCode = async (data: any) => {
    setValidationError("");

    try {
      const result = await requestOtp(data.email, redirectUrl);

      if (result.isSuccess) {
        setSubmittedEmail(data.email);
        setIsSubmitted(true);
        Logger.debug("OTP request sent to", data.email);

        if (onSuccess) {
          onSuccess();
        } else {
          // Redirect to OTP validation page
          await router.push({
            pathname: "/otp",
            query: {
              email: encodeURIComponent(data.email),
              ...(redirectUrl && { next: encodeURIComponent(redirectUrl) }),
            },
          });
        }
      } else if (result.isUserNotFound) {
        setValidationError(t("otp.emailNotRegistered"));
      } else {
        setValidationError(t("otp.requestFailed"));
      }
    } catch (err) {
      Logger.error("Error requesting OTP", err);
      setValidationError(t("otp.requestFailed"));
    }
  };

  const successMessage = (
    <Stack direction="column" gap={12}>
      <Paragraph variant="p1" className={styles.Text}>
        {t("otp.otpSentTo", { email: submittedEmail })}
      </Paragraph>
      <Paragraph variant="p2" className={styles.Text}>
        {t("otp.codeExpires", { minutes: 10 })}
      </Paragraph>
    </Stack>
  );

  const emailForm = (
    <Stack direction="column" gap={24}>
      <form onSubmit={handleSubmit(sendCode)}>
        <Stack direction="column" gap={24}>
          <Input
            label={t("otp.email") as string}
            type="email"
            placeholder="you@example.com"
            errors={errors}
            light
            autoComplete="email"
            {...register("email")}
          />
          {validationError && (
            <Paragraph variant="p3" className={styles.ErrorMessage}>
              {validationError}
            </Paragraph>
          )}
          <Button
            variant="primary"
            disabled={isSubmitting || !email}
            fullWidth
          >
            {t("otp.sendCode")}
          </Button>
        </Stack>
      </form>
      <Paragraph variant="p2" className={styles.Text}>
        {t("otp.noAccountNeeded")}
      </Paragraph>
    </Stack>
  );

  return (
    <Card className={styles.OtpRequestForm}>
      <Heading variant="h2" className={styles.Text}>
        {t("otp.signInWithOtp")}
      </Heading>
      <Paragraph variant="p1" className={styles.Text}>
        {t("otp.enterEmailToGetCode")}
      </Paragraph>
      {isSubmitted ? successMessage : emailForm}
    </Card>
  );
};
